import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiPlus, mdiPencil, mdiDelete, mdiContentSave, mdiClose } from '@mdi/js';
import { CoolantFluid } from '../../types';
import { apiService } from '../../services/apiService';
import { DataTable, Column } from '../components/DataTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { adminTheme } from '../styles/adminTheme';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${adminTheme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${adminTheme.colors.primaryHover};
  }
`;

const FormCard = styled.div`
  background: ${adminTheme.colors.cardBg};
  border: 1px solid ${adminTheme.colors.primary};
  border-radius: ${adminTheme.borderRadius};
  padding: 20px;
  margin-bottom: 16px;
`;

const FormTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 12px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  align-items: flex-end;
`;

const FormGroup = styled.div`
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${adminTheme.colors.textSecondary};
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  min-height: 50px;
  resize: vertical;
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid ${p => p.$primary ? adminTheme.colors.primary : adminTheme.colors.border};
  background: ${p => p.$primary ? adminTheme.colors.primary : 'white'};
  color: ${p => p.$primary ? 'white' : adminTheme.colors.text};

  &:hover {
    opacity: 0.9;
  }
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${p => p.$danger ? adminTheme.colors.danger : adminTheme.colors.textSecondary};

  &:hover {
    background: ${p => p.$danger ? '#fce8e6' : '#f1f3f4'};
  }
`;

const BuiltinBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  background: #e6f4ea;
  color: #0d652d;
`;

export const FluidsPage: React.FC = () => {
  const [fluids, setFluids] = useState<CoolantFluid[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [deleteTarget, setDeleteTarget] = useState<CoolantFluid | null>(null);
  const { toast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    apiService.getFluids()
      .then(setFluids)
      .catch(() => toast('Failed to load fluids', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (fluid: CoolantFluid) => {
    setEditId(fluid.id);
    setFormData({ name: fluid.name, description: fluid.description });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await apiService.updateFluid(editId, formData);
        toast('Fluid updated', 'success');
      } else {
        await apiService.createFluid(formData);
        toast('Fluid created', 'success');
      }
      setShowForm(false);
      setEditId(null);
      setFormData({ name: '', description: '' });
      load();
    } catch (e: any) {
      toast(e.message || 'Failed to save', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteFluid(deleteTarget.id);
      toast('Fluid deleted', 'success');
      setDeleteTarget(null);
      load();
    } catch {
      toast('Failed to delete', 'error');
    }
  };

  const columns: Column<CoolantFluid>[] = [
    { key: 'name', header: 'Name', sortable: true },
    { key: 'description', header: 'Description' },
    {
      key: 'is_builtin',
      header: 'Type',
      render: (row) => row.is_builtin ? <BuiltinBadge>Built-in</BuiltinBadge> : <span style={{ fontSize: 12, color: adminTheme.colors.textMuted }}>Custom</span>,
    },
    {
      key: 'property_count',
      header: 'Points',
      render: (row) => <span>{(row as any).property_count ?? row.properties?.length ?? 0} points</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '80px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          <ActionBtn title="Edit" onClick={() => handleEdit(row)}>
            <Icon path={mdiPencil} size={0.6} />
          </ActionBtn>
          {!row.is_builtin && (
            <ActionBtn $danger title="Delete" onClick={() => setDeleteTarget(row)}>
              <Icon path={mdiDelete} size={0.6} />
            </ActionBtn>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader>
        <PageTitle>Fluids</PageTitle>
        <AddBtn onClick={() => { setShowForm(true); setEditId(null); setFormData({ name: '', description: '' }); }}>
          <Icon path={mdiPlus} size={0.65} />
          Add Fluid
        </AddBtn>
      </PageHeader>

      {showForm && (
        <FormCard>
          <FormTitle>{editId ? 'Edit Fluid' : 'New Fluid'}</FormTitle>
          <FormRow>
            <FormGroup>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </FormGroup>
          </FormRow>
          <FormGroup>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </FormGroup>
          <FormActions>
            <Btn $primary onClick={handleSave}>
              <Icon path={mdiContentSave} size={0.55} />
              Save
            </Btn>
            <Btn onClick={() => { setShowForm(false); setEditId(null); }}>
              <Icon path={mdiClose} size={0.55} />
              Cancel
            </Btn>
          </FormActions>
        </FormCard>
      )}

      <DataTable
        columns={columns}
        data={fluids}
        loading={loading}
        emptyMessage="No fluids defined"
      />

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Fluid"
          message={`Delete "${deleteTarget.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
